/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, Building2, Globe, Filter, MapPin, Phone, ArrowRight, Star, Award } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { MembershipBadge } from '../components/MembershipSeal';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

import Img from '../components/Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const industryLabels = {
  restaurants: 'Restaurants & Hospitality', professional: 'Professional Services', health: 'Health & Wellness',
  real_estate: 'Real Estate', finance: 'Finance', marketing: 'Marketing & Media', technology: 'Technology',
  education: 'Education', retail: 'Retail', community: 'Community Organizations', other: 'Other'
};

const MembersDirectoryPage = () => {
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchMembers(); }, [search, selectedCategory]);

  const fetchCategories = async () => {
    try { const res = await axios.get(`${API}/member-categories`); setCategories(res.data); } catch { /* */ }
  };

  const fetchMembers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
      const res = await axios.get(`${API}/members?${params.toString()}`);
      setMembers(res.data);
    } catch { /* */ }
  };

  return (
    <div className="min-h-screen pt-16 sm:pt-20 bg-[#F8F9FB]" data-testid="members-directory-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="bg-gradient-to-br from-[#002249] via-[#1D3557] to-[#457B9D] py-14 sm:py-20">
          <div className="max-w-6xl mx-auto px-5 sm:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
              <span className="text-xs sm:text-sm font-bold text-[#E63946] tracking-wider uppercase mb-3 block">Members Directory</span>
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" data-testid="members-hero-title">Our Business Community</h1>
              <p className="text-white/65 text-sm sm:text-base lg:text-lg leading-relaxed">
                Discover Dominican-owned and operated businesses across Florida. Each listing is maintained directly by the business owner.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Search & Filter */}
      <section className="bg-white border-b border-slate-200 sticky top-16 sm:top-[72px] md:top-20 z-30" data-testid="members-filter">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search businesses..."
                className="pl-10 h-11 rounded-lg border-slate-200" data-testid="members-search" />
            </div>
            <div className="w-full sm:w-56">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="h-11 rounded-lg border-slate-200" data-testid="members-category-filter">
                  <Filter className="w-3.5 h-3.5 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>)}
                  {Object.entries(industryLabels).map(([k, v]) => (
                    !categories.find(c => c.id === k) && <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Directory Grid */}
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10" data-testid="members-grid">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-slate-500">
            <span className="font-semibold text-[#002249]">{members.length}</span> businesses
          </p>
        </div>

        {members.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {members.map((member, i) => {
              const catLabel = industryLabels[member.category] || categories.find(c => c.id === member.category)?.name || member.category?.replace('_', ' ') || '';
              return (
                <motion.div key={member.id} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i * 0.03, 0.15) }}>
                  <Link to={`/business/${member.id}`} className="block bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all group" data-testid={`member-card-${i}`}>
                    {/* Logo area */}
                    <div className="h-40 bg-slate-50 relative flex items-center justify-center p-6 overflow-hidden">
                      {member.logo_url ? (
                        <Img variant="business" src={resolveImg(member.logo_url)} alt={member.business_name} className="max-h-full max-w-full object-contain" />
                      ) : (
                        <div className="w-20 h-20 bg-gradient-to-br from-[#002249] to-[#457B9D] rounded-xl flex items-center justify-center">
                          <Building2 className="w-10 h-10 text-white/60" />
                        </div>
                      )}
                      {member.is_featured && (
                        <span className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#E63946] text-white text-[10px] font-bold px-2 py-1 rounded-md">
                          <Star className="w-3 h-3" /> Featured
                        </span>
                      )}
                      {member.badges?.includes('founding_member') && (
                        <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-[10px] font-bold px-2 py-1 rounded-md shadow-sm" data-testid={`founding-badge-${i}`}>
                          <Award className="w-3 h-3" /> Founding
                        </span>
                      )}
                    </div>
                    {/* Info */}
                    <div className="p-4 sm:p-5">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="text-[15px] font-bold text-[#002249] group-hover:text-[#E63946] transition-colors leading-tight truncate flex-1">{member.business_name}</h3>
                        {member.membership_plan && <MembershipBadge plan={member.membership_plan} />}
                      </div>
                      {catLabel && <p className="text-[12px] font-medium text-[#E63946] mt-1">{catLabel}</p>}
                      {member.description && <p className="text-[13px] text-slate-600 mt-2 line-clamp-2 leading-relaxed">{member.description}</p>}
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                        <div className="flex items-center gap-3">
                          {member.address && <span className="text-[11px] text-slate-400 flex items-center gap-1 truncate"><MapPin className="w-3 h-3 flex-shrink-0" />{member.address.split(',')[0]}</span>}
                          {member.website && <span className="text-[11px] text-slate-400 flex items-center gap-1"><Globe className="w-3 h-3" /></span>}
                        </div>
                        <span className="text-[12px] font-semibold text-[#002249] group-hover:text-[#E63946] transition-colors flex items-center">View <ArrowRight className="w-3 h-3 ml-0.5" /></span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-semibold text-[#002249]">No businesses found</p>
            <p className="text-sm text-slate-500 mt-1">{search ? 'Try a different search term.' : 'Check back soon as our directory grows.'}</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default MembersDirectoryPage;
